/**
 * SubscriptionsDashboard Component
 * Main container for subscriptions management with accordion-based layout
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { SubscriptionAccordion } from './SubscriptionAccordion';
import { SUBSCRIPTION_TYPE_METADATA, SubscriptionType } from '../types/SubscriptionTypes';
import { SUBSCRIPTIONS_DASHBOARD_COPY, storedUiLanguage } from '../i18n/consoleChromeCopy';
import './SubscriptionsDashboard.css';

interface SubscriptionsDashboardProps {
  onSubscriptionUpdate?: (subscriptionId: string) => void;
  showAuditLog?: boolean;
}

export const SubscriptionsDashboard: React.FC<SubscriptionsDashboardProps> = ({
  onSubscriptionUpdate,
  showAuditLog = false
}) => {
  const { state, toggleAccordion } = useSubscriptions();
  const chrome = SUBSCRIPTIONS_DASHBOARD_COPY[storedUiLanguage()];

  // Get sorted subscription types with content
  const subscriptionTypes: SubscriptionType[] = useMemo(
    () => ['social-media', 'legal-search', 'database', 'commercial-credit', 'saas'] as SubscriptionType[],
    []
  );

  if (state.isLoading) {
    return (
      <div className="subscriptions-dashboard subscriptions-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{chrome.loading}</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="subscriptions-dashboard subscriptions-error">
        <div className="error-message">
          <h3>{chrome.errorTitle}</h3>
          <p>{state.error}</p>
          <button onClick={() => window.location.reload()}>{chrome.retry}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscriptions-dashboard">
      <div className="subscriptions-header">
        <h2>{chrome.title}</h2>
        <p className="subscriptions-subtitle">
          {chrome.subtitle}
        </p>
        <div className="subscriptions-header-actions">
          <Link to="/console/billing" className="subscriptions-cross-link">
            {chrome.billingLink}
          </Link>
        </div>
      </div>

      <div className="subscriptions-content">
        {subscriptionTypes.map(type => {
          const subscriptions = state.subscriptionsByType[type];
          const metadata = SUBSCRIPTION_TYPE_METADATA[type];
          const isExpanded = state.expandedAccordions.has(type);

          // Only render accordion if there are subscriptions of this type
          if (subscriptions.length === 0) {
            return null;
          }

          return (
            <SubscriptionAccordion
              key={type}
              type={type}
              subscriptions={subscriptions}
              metadata={metadata}
              isExpanded={isExpanded}
              onToggle={() => toggleAccordion(type)}
              onSubscriptionUpdate={onSubscriptionUpdate}
            />
          );
        })}

        {state.subscriptions.length === 0 && (
          <div className="subscriptions-empty">
            <p>{chrome.empty}</p>
            <p className="subscriptions-empty-hint">{chrome.emptyHint}</p>
          </div>
        )}
      </div>

      {showAuditLog && state.auditLog.length > 0 && (
        <div className="subscriptions-audit-log">
          <h3>{chrome.recentActivity}</h3>
          <div className="audit-log-entries">
            {state.auditLog.slice(0, 10).map(entry => (
              <div key={entry.id} className={`audit-entry audit-${entry.status}`}>
                <span className="audit-action">{entry.action}</span>
                <span className="audit-timestamp">
                  {new Date(entry.timestamp).toLocaleDateString()} {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsDashboard;
